import * as stylex from '@stylexjs/stylex';
import { useMutation, useQuery } from '@tanstack/react-query';
import { createFileRoute, Outlet, redirect, useChildMatches } from '@tanstack/react-router';

import { ErrorState } from '@/components/error-state.component';
import { Header } from '@/components/header.component';
import { InterestsDialog } from '@/components/interests-dialog.component';
import { NotFoundState } from '@/components/not-found-state.component';
import { useSyncTheme } from '@/hooks/use-sync-theme.hook';
import { ProgressIndicator, Text, useMeasure, useMediaQuery } from '@/libs/serenity-ui';
import { breakpoints } from '@/libs/serenity-ui/constants.stylex';
import { $ } from '@/libs/serenity-ui/tokens.stylex';
import { userMutations, userQueries } from '@/resources/user/user.api';
import { tryCatch } from '@/utils/try-catch.util';

export const Route = createFileRoute('/_app')({
  component: RouteComponent,
  notFoundComponent: NotFoundState,
  beforeLoad: ({ context }) => {
    if (!localStorage.getItem('access_token')) {
      throw redirect({ to: '/sign-in' });
    }
    tryCatch(context.queryClient.ensureQueryData(userQueries.me));
  },
  errorComponent: (props) => <ErrorState withHeader {...props} />,
  pendingComponent: PendingState,
  head: () => ({
    meta: [
      {
        name: 'theme-color',
        media: '(prefers-color-scheme: light)',
        content: '#FFFFFF',
      },
      {
        name: 'theme-color',
        media: '(prefers-color-scheme: dark)',
        content: '#09090A',
      }
    ],
  })
});

function RouteComponent() {
  const [measureRef, dimensions] = useMeasure();
  const isDesktop = useMediaQuery(breakpoints.large);

  const matches = useChildMatches();

  const updateInterestsMutation = useMutation(userMutations.updateInterests);

  const userInterestsQuery = useQuery(userQueries.interests);

  useSyncTheme();

  const ContextBar = matches.at(-1)?.staticData?.contextBarComponent;

  return (
    <>
      <Header ref={measureRef}>
        {ContextBar && <ContextBar />}
      </Header>
      {dimensions.height &&
        <main {...stylex.props([styles.base, styles.paddingTop(dimensions.height + (isDesktop ? 32 : 0))])}>
          <Outlet />
        </main>}
      <InterestsDialog
        isOpen={userInterestsQuery.data?.length === 0}
        onSubmit={(value) => updateInterestsMutation.mutateAsync({ interest_ids: value })}
      />
    </>
  );
}

function PendingState() {
  const [measureRef, dimensions] = useMeasure();
  const isDesktop = useMediaQuery(breakpoints.large);

  const matches = useChildMatches();

  const ContextBarPendingComponent = matches.at(-1)?.staticData?.contextBarPendingComponent;
  const pendingDescription = matches.at(-1)?.staticData?.pendingDescription;

  return (
    <>
      <Header isPending ref={measureRef}>
        {ContextBarPendingComponent && <ContextBarPendingComponent />}
      </Header>
      {dimensions.height &&
        <main {...stylex.props([styles.base, styles.paddingTop(dimensions.height + (isDesktop ? 32 : 16)), pendingStyles.base])}>
          <ProgressIndicator size={24} />
          {pendingDescription && <Text color="placeholder" size={16}>{pendingDescription}</Text>}
        </main>}
    </>
  );
}

const styles = stylex.create({
  base: {
    alignItems: 'center',
    backgroundColor: $.surfaceBGPrimary,
    display: 'flex',
    flexDirection: 'column',
    gap: 32,
    height: 'fit-content',
    marginLeft: 'auto',
    marginRight: 'auto',
    minHeight: '100%',
    paddingBottom: 100,

    paddingTop: {
      default: 132,
      [breakpoints.large]: 148,
    },
    width: {
      default: '100%',
      [breakpoints.large]: 728,
    },
  },
  paddingTop: (paddingTop: number) => ({
    paddingTop,
  }),
});

const pendingStyles = stylex.create({
  base: {
    gap: 8,
    minHeight: 'fit-content',
    width: '100%',
  },
});
